import { formatSsn } from "../../../lib/format";

/** Which applicant a bureau view request targets. */
export type BureauTarget = "driver" | "secondSigner";

/** Which applicant a repull targets (one applicant per call). */
export type RepullTarget = BureauTarget;

/** Raw sales-person shape nested in the `getBureauView` response. */
export interface BureauSalesPersonResponse {
  firstName?: string;
  lastName?: string;
}

/** Raw single-bureau-report shape nested in the `getBureauView` response. */
export interface BureauReportResponse {
  bureauCode?: string;
  bureauName?: string;
  reportText?: string;
  reportTextAvailable?: boolean;
}

/**
 * Real BFF response shape for `BureauApi.getBureauView` (GET
 * /api/v1/workbench/applications/{approvalId}/applicants/{applicantId}/bureau).
 * The generated client types this endpoint's response as bare `object` — no
 * OpenAPI schema exists yet (OR-23155) — so this interface is hand-written
 * directly off a real payload captured from the dev proxy. One call returns
 * exactly one applicant's bureau data (score, frozen flag, single report),
 * enriched with the sales person who sold the deal.
 */
export interface BureauViewResponse {
  approvalId?: number;
  applicantId?: number;
  /** CAPS credit score, returned as a numeric string, e.g. "523". */
  capsCreditScore?: string;
  frozen?: boolean;
  bureau?: BureauReportResponse;
  isDriver?: boolean;
  salesPerson?: BureauSalesPersonResponse;
}

/**
 * Applicant identity sourced from the DSC Workbench dashboard response
 * (`DscApplicantSection.applicantId` + residence name/SSN) — the bureau
 * endpoint itself only returns score/report/frozen data, not name or SSN.
 */
export interface BureauApplicantInput {
  applicantId: string;
  name: string;
  ssn: string;
}

/** A driver or 2nd-signer's bureau-relevant details, normalized for the UI. */
export interface BureauApplicantView {
  name: string;
  ssn: string;
  capsCreditScore?: number;
}

/** One selectable bureau report and its read-only content. */
export interface BureauReportView {
  id: string;
  label: string;
  content: string;
}

/** Full modal payload: header/panel info and the driver's + 2nd signer's reports. */
export interface BureauDataView {
  salesPerson: string;
  approvalNumber: string;
  driver: BureauApplicantView;
  /** Undefined when the deal has no co-maker — the 2nd signer section is omitted, not blanked. */
  secondSigner?: BureauApplicantView;
  reports: BureauReportView[];
}

/**
 * Builds the display name for a bureau view's sales person, "LAST, FIRST"
 * (matches the legacy Step6 JSF popup format).
 * @param {BureauSalesPersonResponse | undefined} raw - The sales person from the BFF response.
 * @returns {string} The formatted display name, or "" when absent.
 */
export function toSalesPersonName(raw: BureauSalesPersonResponse | undefined): string {
  if (!raw?.lastName && !raw?.firstName) {
    return "";
  }
  return [raw.lastName, raw.firstName].filter(Boolean).join(", ");
}

/**
 * Merges a caller-supplied applicant identity (name/SSN from the dashboard)
 * with the bureau endpoint's CAPS credit score for that same applicant.
 * @param {BureauApplicantInput} input - The applicant's name/SSN/id.
 * @param {BureauViewResponse | undefined} response - The bureau response for that applicant.
 * @returns {BureauApplicantView} The normalized applicant view model.
 */
export function toBureauApplicant(
  input: BureauApplicantInput,
  response: BureauViewResponse | undefined
): BureauApplicantView {
  const parsedScore = response?.capsCreditScore ? Number(response.capsCreditScore) : undefined;
  return {
    name: input.name,
    ssn: formatSsn(input.ssn),
    capsCreditScore: parsedScore === undefined || Number.isNaN(parsedScore) ? undefined : parsedScore,
  };
}

/**
 * Builds the single selectable report entry for one applicant's bureau
 * response, prefixed with a Driver/2nd Signer label so both applicants'
 * reports can coexist in the same dropdown.
 * @param {string} label - "Driver" or "2nd Signer", already translated.
 * @param {BureauViewResponse | undefined} response - The bureau response for that applicant.
 * @returns {BureauReportView | undefined} The report entry, or undefined when no report is available.
 */
export function toBureauReport(label: string, response: BureauViewResponse | undefined): BureauReportView | undefined {
  const report = response?.bureau;
  if (!report?.reportTextAvailable) {
    return undefined;
  }
  return {
    id: `${response?.applicantId ?? ""}-${report.bureauCode ?? ""}`,
    label: report.bureauName ? `${label} - ${report.bureauName}` : label,
    content: report.reportText ?? "",
  };
}
